import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Search, ShieldCheck, BadgeCheck, Star, Phone, Mail, MapPin, Truck,
  FileText, CheckCircle2, Award, Briefcase, Loader2, X, Filter, User,
  Send, UserCheck, ChevronRight
} from "lucide-react";

import Sidebar from "../Includes/Sidebar";
import TopBar from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

// ─── HELPER: FORMAT NAME FOR PRIVACY (e.g. "Ujjwal G.") ────────────────────────
const formatDriverName = (fullName) => {
  if (!fullName) return "Verified Driver";
  const nameParts = fullName.trim().split(" ");
  if (nameParts.length > 1) {
    const first = nameParts[0];
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${first} ${lastInitial}.`;
  }
  return fullName;
};

// ─── DRIVER DETAIL MODAL ──────────────────────────────────────────────────────
function DriverDetailModal({ driver, onClose, onInvite }) {
  if (!driver) return null;

  // 🛡️ BULLETPROOF DATA CHECKS: Catches the data no matter what the backend named it
  const vehicleDisplay = driver.vehicle || driver.vehicle_info || (driver.vehicle_make ? `${driver.vehicle_make} ${driver.vehicle_model}`.trim() : "Pilot Car");
  const expDisplay = driver.experience_years || driver.years_of_experience || driver.years_exp || driver.experience || "0";
  const loadsDisplay = driver.completed_loads || driver.total_loads || driver.loads_run || "0";

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="p-5 border-b border-gray-100 flex items-start justify-between bg-white relative">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-50 border-2 border-blue-100 rounded-2xl flex items-center justify-center text-blue-700 font-extrabold text-xl flex-shrink-0">
              {driver.initials || "DR"}
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                {formatDriverName(driver.full_name)}
                <BadgeCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
              </h2>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                {driver.location || "Available Nationwide"}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-6 bg-gray-50/50">
          
          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white p-3 rounded-xl border border-gray-100 text-center shadow-sm">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500 mx-auto mb-1" />
              <p className="text-base font-bold text-gray-900">{driver.rating || "4.9"}</p>
              <p className="text-[9px] font-bold text-gray-400 uppercase">Rating</p>
            </div>
            <div className="bg-white p-3 rounded-xl border border-gray-100 text-center shadow-sm">
              <Briefcase className="w-4 h-4 text-blue-500 mx-auto mb-1" />
              <p className="text-base font-bold text-gray-900">{expDisplay} Years</p>
              <p className="text-[9px] font-bold text-gray-400 uppercase">Experience</p>
            </div>
            <div className="bg-white p-3 rounded-xl border border-gray-100 text-center shadow-sm">
              <Truck className="w-4 h-4 text-indigo-500 mx-auto mb-1" />
              <p className="text-base font-bold text-gray-900">{loadsDisplay}</p>
              <p className="text-[9px] font-bold text-gray-400 uppercase">Loads Run</p>
            </div>
            <div className="bg-white p-3 rounded-xl border border-gray-100 text-center shadow-sm">
              <Award className="w-4 h-4 text-green-500 mx-auto mb-1" />
              <p className="text-base font-bold text-gray-900">100%</p>
              <p className="text-[9px] font-bold text-gray-400 uppercase">Safety Record</p>
            </div>
          </div>

          {/* Vehicle & Contact Info */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Driver Specifications</p>
            <div className="bg-white p-4 rounded-xl border border-gray-100 space-y-3 shadow-sm">
              <div className="flex justify-between items-center text-xs border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Vehicle Specs</span>
                <span className="font-bold text-gray-900">{vehicleDisplay}</span>
              </div>
              <div className="flex justify-between items-center text-xs border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Phone Contact</span>
                <span className="font-bold text-gray-900 flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5 text-green-600" /> {driver.phone || "Verified On File"}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Email Address</span>
                <span className="font-bold text-gray-900 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5 text-blue-600" /> {driver.email}
                </span>
              </div>
            </div>
          </div>

          {/* Verification Checks */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Verified Compliance Badges</p>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-gray-700 font-medium">
                  <FileText className="w-4 h-4 text-blue-500" /> $1,000,000 Commercial Auto Liability
                </span>
                <span className="font-bold text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Active
                </span>
              </div>
              <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100">
                <span className="flex items-center gap-2 text-gray-700 font-medium">
                  <ShieldCheck className="w-4 h-4 text-purple-500" /> PPCA / PEVO Escort Certification
                </span>
                <span className="font-bold text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                </span>
              </div>
              <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100">
                <span className="flex items-center gap-2 text-gray-700 font-medium">
                  <BadgeCheck className="w-4 h-4 text-teal-500" /> Background Check & Driving Record
                </span>
                <span className="font-bold text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Passed
                </span>
              </div>
            </div>
          </div>

        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-100 bg-white flex items-center justify-between gap-3">
          <button 
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-xs font-bold hover:bg-gray-50 transition"
          >
            Close
          </button>
          <div className="flex gap-2">
            {driver.phone && (
              <a 
                href={`tel:${driver.phone}`} 
                className="px-4 py-2.5 bg-green-50 border border-green-200 text-green-700 text-xs font-bold rounded-xl hover:bg-green-100 transition flex items-center gap-1.5"
              >
                <Phone className="w-3.5 h-3.5" /> Call Driver
              </a>
            )}
            <button 
              onClick={() => { onClose(); onInvite(driver); }}
              className="px-5 py-2.5 bg-[#1a2744] text-white text-xs font-bold rounded-xl hover:opacity-90 transition flex items-center gap-1.5 shadow-sm"
            >
              <Send className="w-3.5 h-3.5" /> Invite To Load
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── MAIN VERIFIED DRIVERS PAGE ───────────────────────────────────────────────
export default function VerifiedCompanyDriversPage() {
  const navigate = useNavigate();

  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");

  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Multi-select: a job's route can touch several states (pickup, drop-off,
  // states in between), so a company needs to match drivers against ALL of
  // them at once, not just one. Empty array = no state filter = show everyone.
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/company-login");
  };

  // Auth Guard
  useEffect(() => {
    if (!user || !token) navigate("/company-login", { replace: true });
  }, [user, token, navigate]);

  // Fetch Drivers
  useEffect(() => {
    const fetchVerifiedDrivers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/drivers/verified/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setDrivers(data);
        }
      } catch (err) {
        console.error("Error fetching drivers:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) fetchVerifiedDrivers();
  }, [token]);

  const companyName = user?.company_name || user?.name || "Company";
  const initials = companyName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "CO";

  // Toggle one state in/out of the selection. Clicking an already-selected
  // state removes it; clicking "ALL" clears the whole selection.
  const toggleState = (st) => {
    if (st === "ALL") {
      setSelectedStates([]);
      return;
    }
    setSelectedStates(prev =>
      prev.includes(st) ? prev.filter(s => s !== st) : [...prev, st]
    );
  };

  // Filter Drivers — matches if the driver's location contains ANY of the
  // selected states (so a company can cover a whole multi-state route in
  // one search: e.g. select TX + AZ + CA for a Dallas -> LA run).
  const filteredDrivers = drivers.filter(d => {
    const query = searchQuery.toLowerCase();
    const matchSearch = !query || 
      d.full_name?.toLowerCase().includes(query) ||
      d.location?.toLowerCase().includes(query) ||
      d.vehicle?.toLowerCase().includes(query);

    const matchState = selectedStates.length === 0 ||
      selectedStates.some(st => d.location?.toUpperCase().includes(st));

    return matchSearch && matchState;
  });

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <Sidebar
        activeNav="drivers"
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        initials={initials}
        companyName={companyName}
        handleLogout={handleLogout}
      />

      {/* Main Content Shell */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar
          title="Verified Drivers"
          setSidebarOpen={setSidebarOpen}
          initials={initials}
          notifCount={2}
          handleLogout={handleLogout}
        />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Banner Header */}
            <div className="bg-[#1a2744] rounded-2xl p-5 sm:p-6 text-white shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <BadgeCheck className="w-5 h-5 text-green-400" />
                  <h1 className="text-lg sm:text-xl font-extrabold">Verified Driver Directory</h1>
                </div>
                <p className="text-xs sm:text-sm text-blue-200">
                  Directly dispatch to certified, background-checked pilot car operators nationwide.
                  Select every state your route touches to match the right drivers.
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-center flex-shrink-0">
                <span className="text-[10px] uppercase font-bold text-blue-200 block">Verified Operators</span>
                <span className="text-xl font-black">{drivers.length}</span>
              </div>
            </div>

            {/* Controls Bar (Search + State Chips) */}
            <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search driver by name, city, or equipment type..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {/* State Filter Chips — multi-select */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-1 scrollbar-none">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1 mr-1 flex-shrink-0">
                  <Filter className="w-3 h-3" /> States{selectedStates.length > 0 ? ` (${selectedStates.length})` : ""}:
                </span>
                <button
                  onClick={() => toggleState("ALL")}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold transition flex-shrink-0 ${
                    selectedStates.length === 0
                      ? "bg-[#1a2744] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  ALL
                </button>
                {US_STATES.map(st => {
                  const active = selectedStates.includes(st);
                  return (
                    <button
                      key={st}
                      onClick={() => toggleState(st)}
                      aria-pressed={active}
                      className={`px-3 py-1 rounded-lg text-[10px] font-bold transition flex-shrink-0 flex items-center gap-1 ${
                        active
                          ? "bg-emerald-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {active && <CheckCircle2 className="w-2.5 h-2.5" />}
                      {st}
                    </button>
                  );
                })}
                {selectedStates.length > 0 && (
                  <button
                    onClick={() => setSelectedStates([])}
                    className="px-3 py-1 rounded-lg text-[10px] font-bold text-red-600 hover:bg-red-50 transition flex-shrink-0 flex items-center gap-1"
                  >
                    <X className="w-2.5 h-2.5" /> Clear
                  </button>
                )}
              </div>

              {/* Active selection recap, so it's clear at a glance what's being matched */}
              {selectedStates.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-gray-100">
                  <span className="text-[10px] font-semibold text-gray-400">Matching drivers in:</span>
                  {selectedStates.map(st => (
                    <span key={st} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {st}
                      <button onClick={() => toggleState(st)} className="hover:text-emerald-900">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Drivers Grid */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-2" />
                <p className="text-xs text-gray-400 font-medium">Loading verified directory...</p>
              </div>
            ) : filteredDrivers.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                <UserCheck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <h3 className="text-sm font-bold text-gray-800">No Drivers Found</h3>
                <p className="text-xs text-gray-400 mt-1">Try clearing a state or your search filter.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDrivers.map(driver => {
                  // 🛡️ BULLETPROOF DATA CHECKS: Also used in the main mapping list
                  const vehicleDisplay = driver.vehicle || driver.vehicle_info || (driver.vehicle_make ? `${driver.vehicle_make} ${driver.vehicle_model}`.trim() : "Pilot Car");
                  const expDisplay = driver.experience_years || driver.years_of_experience || driver.years_exp || driver.experience || "0";

                  return (
                    <div 
                      key={driver.id}
                      onClick={() => setSelectedDriver(driver)}
                      className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        {/* Driver Card Header */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-12 h-12 bg-blue-50 text-blue-700 font-extrabold text-sm rounded-xl flex items-center justify-center border border-blue-100 flex-shrink-0">
                              {driver.initials || "DR"}
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-bold text-gray-900 truncate flex items-center gap-1">
                                {formatDriverName(driver.full_name)}
                                <BadgeCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                              </h3>
                              <p className="text-[11px] text-gray-400 truncate flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3 text-gray-300" />
                                {driver.location || "Nationwide"}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
                              <Star className="w-3 h-3 fill-amber-400 stroke-amber-400" /> {driver.rating || "4.9"}
                            </span>
                            {/* PPCA CERTIFIED BADGE */}
                            {driver.is_badge_verified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                                <ShieldCheck className="w-2.5 h-2.5" /> PPCA Certified
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Specs */}
                        <div className="bg-gray-50 rounded-xl p-2.5 border border-gray-100 grid grid-cols-2 gap-2 text-[11px] mb-4">
                          <div>
                            <span className="text-[9px] text-gray-400 font-semibold block uppercase">Vehicle</span>
                            <span className="font-bold text-gray-800 truncate block">{vehicleDisplay}</span>
                          </div>
                          <div>
                            <span className="text-[9px] text-gray-400 font-semibold block uppercase">Experience</span>
                            <span className="font-bold text-gray-800 block">{expDisplay} Years</span>
                          </div>
                        </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className="text-[11px] font-bold text-blue-600 flex items-center gap-1 group-hover:underline">
                          View Credentials <ChevronRight className="w-3.5 h-3.5" />
                        </span>

                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/my-loads");
                          }}
                          className="px-4 py-1.5 bg-[#1a2744] text-white text-[11px] font-bold rounded-xl hover:opacity-90 transition flex items-center gap-1"
                        >
                          <Send className="w-3 h-3" /> Invite
                        </button>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </main>

        <BottomNav activeNav="drivers" setSidebarOpen={setSidebarOpen} handleLogout={handleLogout}/>
      </div>

      {/* Driver Inspector Modal */}
      {selectedDriver && (
        <DriverDetailModal 
          driver={selectedDriver} 
          onClose={() => setSelectedDriver(null)}
          onInvite={() => {
            setSelectedDriver(null);
            navigate("/my-loads");
          }}
        />
      )}
    </div>
  );
}
